import { debounce } from "lodash";
import { Grid } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ENV from "../../../config/config";
import EmptyList from "../../misc/EmptyList";
import { colors } from "../../css components/Style";
import { toInternationalFormat } from "../../../utils";
import { useAxios, useComponent } from "../../../hooks";
import { Loader, PageHeader } from "../../../components";
import { alert } from "../../../redux/slices/alertSlice";
import Pagination from "../../../components/Pagination";
import { PrimaryLoadingButton } from "../../css components/Button";
import RequestedList from "../campaign list components/CampaignRequestList";
import SortBy from "../campaign list components/campaign dp list components/SortBy";
import Search from "./../campaign list components/campaign dp list components/Search";
import Categorize from "../campaign list components/campaign dp list components/Categorize";

export default function CampaignDP() {
  const axios = useAxios();
  const dispatch = useDispatch();
  const { loader } = useComponent();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortList, setSortList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [categorize, setCategorize] = useState("");

  const [lastPage, setLastPage] = useState({ totalCount: 0, lastPage: false });

  const { activeBrand, userID } = useSelector((state) => state.memberDetails);

  let options = [
    {
      value: "Campaign_Status",
      label: "Campaign Status",
      color: "#333",
    },
    { value: "Approval_Status", label: "Approval Status", color: "#333" },
  ];

  useEffect(() => {
    if (categorize === "Campaign_Status") {
      setSortList([
        { value: "", label: "All" },
        { value: "REQUESTED", label: "Requested", color: "#333" },
        { value: "ACCEPTED", label: "Accepted", color: "#333" },
        {
          value: "PARTIAL_ACCEPTED",
          label: "Partial Accepted",
          color: colors.success,
        },
        { value: "REJECTED", label: "Rejected", color: colors.error },
        {
          value: "PARTIAL_REJECTED",
          label: "Partial Rejected",
          color: colors.error,
        },
        { value: "READY", label: "Ready", color: colors.success },
        { value: "ACTIVE", label: "Active", color: colors.success },
        { value: "EXPIRED", label: "Expired", color: colors.error },
      ]);
    }

    if (categorize === "Approval_Status") {
      setSortList([
        { value: "", label: "All" },
        { value: "REQUESTED", label: "Requested", color: colors.yellow },
        { value: "ACCEPTED", label: "Accepted", color: colors.green },
        { value: "REJECTED", label: "Rejected", color: colors.error },
        { value: "MISSED", label: "Missed", color: colors.error },
      ]);
    }
  }, [categorize]);

  useEffect(() => {
    loader.start(1);

    axios({
      url:
        "/user/showCampaignRequestsDP?brandId=" +
        activeBrand.ID +
        `&page=${page}&limit=10` +
        (searchKey ? `&search=${encodeURIComponent(searchKey)}` : "") +
        (sortBy
          ? `&${
              categorize === "Campaign_Status"
                ? "campaignStatus="
                : "requestStatus="
            }${sortBy}`
          : ""),
    }).then((response) => {
      if (response.status) {
        let campaign = [];

        for (let key of response.data.sortedRequests) {
          let data = {};
          for (let fields of key.campaign_fields) {
            if (
              Object.keys(fields)[0] === "budget_min" ||
              Object.keys(fields)[0] === "budget_max" ||
              Object.keys(fields)[0] === "monthly_active_user" ||
              Object.keys(fields)[0] === "volume"
            ) {
              data[Object.keys(fields)[0]] = toInternationalFormat(
                Object.values(fields)[0]
              );
            } else {
              data[Object.keys(fields)[0]] = Object.values(fields)[0];
            }
          }

          campaign.push({
            ...data,
            brand: key.brand_info,
            previewImage: !!key.preview_image
              ? key.preview_image
              : "https://dc-chat-media.s3.ap-south-1.amazonaws.com/NaN_placeholder.png",
            status: key.requestStatus,
            request_id: key.request_id,
            sender_brandId: key.sender_brandId,
            sender_userId: key.sender_userId,
            sender_campaignId: key.sender_campaignId,
            receiver_brandId: key.receiver_brandId,
            receiver_userId: key.receiver_userId,
            isAccepted: key.isAccepted,
            user_Count: key.user_Count,
            campaign_status: key.campaign_status,
          });
        }

        setCampaigns(campaign);

        setLastPage({
          totalCount: response.data.totalCounts,
          lastPage: response.data.sortedRequests.length < 10,
        });

        loader.apiComplete();
      } else {
        loader.apiComplete();
      }
    });
  }, [axios, sortBy, userID, activeBrand.ID, loader, page, searchKey]);

  function handlePagination(step, index) {
    setPage((prev) =>
      step === "index" ? index : prev + (step === "next" ? 1 : -1)
    );
  }

  const handleSearchDebounce = debounce(async (value) => {
    setSearchKey(value);
  }, 300);

  return (
    <Fragment>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageHeader
            icons={[
              <Categorize
                options={options}
                setCategorize={setCategorize}
                categorize={categorize}
                setPage={setPage}
              />,
              <SortBy
                sortBy={sortBy}
                setPage={setPage}
                sortList={sortList}
                setSortBy={setSortBy}
              />,
              <Search
                handleSearchDebounce={handleSearchDebounce}
                searchKey={searchKey}
                setPage={setPage}
                setSearchKey={setSearchKey}
              />,
            ]}
            buttons={[
              campaigns.length > 0 && (
                <PrimaryLoadingButton
                  variant="outlined"
                  size="large"
                  loading={loading}
                  sx={{
                    marginLeft: "5px",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => handleDownloadReport()}
                >
                  Download Report
                </PrimaryLoadingButton>
              ),
            ]}
            pageHeader={"List of campaigns"}
            pageName={"Campaign Offers"}
          />
        </Grid>
      </Grid>

      <Loader height="75%">
        {campaigns.length > 0 ? (
          <RequestedList campaigns={campaigns} component="request" />
        ) : (
          <EmptyList title={"No Request found."} />
        )}
      </Loader>

      {parseInt(lastPage.totalCount) > 0 && (
        <Grid
          style={{ marginTop: "40px" }}
          item
          container
          display={"flex"}
          justifyContent={"center"}
        >
          <Pagination
            count={Math.ceil(lastPage.totalCount / 10)}
            variant="outlined"
            color="secondary"
            page={page}
            onChange={(e, value) => handlePagination("index", value)}
          />
        </Grid>
      )}
    </Fragment>
  );

  function handleDownloadReport() {
    setLoading(true);
    axios({
      url:
        "/contributer/consolidated/report?brand_id=" +
        activeBrand.ID +
        "&campaignStatus=running",
      disableRedirect: true,
    }).then((response) => {
      if (response.status) {
        dispatch(
          alert({
            type: "success",
            message: "Report was successfully downloaded.",
          })
        );

        window.location.href = response?.data?.reportUrl;
      }
      setLoading(false);
    });
  }
}
